import { memo } from 'react';
import { useSelector } from 'react-redux';

import {
  contentCollectionClipsInvalidFieldsSelector,
  contentCollectionRequiredFieldsSelector,
} from '@air/feature-content-collection/redux';
import { DateInput, type DateInputProps } from '@air/primitive-date-input';
import { Label } from '@air/primitive-label';
import { parseAirDateToISO } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import { type ContentCollectionBaseCustomFieldsProps } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldsSelect';
import { type AirState } from '~/store/types';

export type ContentCollectionBaseCustomFieldDateInputProps = {
  onDateSelection: (params: string | undefined) => void;
} & ContentCollectionBaseCustomFieldsProps;

export const ContentCollectionBaseCustomFieldDateInput = memo(
  ({
    field,
    onDateSelection,
    clips,
    disabled = false,
    showDescription = false,
  }: ContentCollectionBaseCustomFieldDateInputProps) => {
    const requiredFields = useSelector(contentCollectionRequiredFieldsSelector);
    const invalidFields = useSelector((st: AirState) =>
      contentCollectionClipsInvalidFieldsSelector(
        st,
        clips.map(({ id }) => id),
      ),
    );

    const showInvalidState = !disabled && invalidFields.some((f) => f.customFieldId === field.id);
    const required = !disabled && requiredFields.some((f) => f.customFieldId === field.id);

    const handleOnChange: DateInputProps['onChange'] = (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      const dateValue = date?.toISOString();
      if (dateValue === field.dateValue) return;
      onDateSelection(dateValue);
    };

    return (
      <div className="flex w-full flex-col gap-1">
        <Label className="flex items-center justify-between font-semibold" htmlFor={field.id}>
          {field.name}
          {required && <span className={cn('text-10 uppercase', showInvalidState && 'text-red-9')}>Required</span>}
        </Label>
        <DateInput
          disabled={disabled}
          showCalendarIcon={false}
          size="large"
          selected={field.dateValue ? parseAirDateToISO(field.dateValue) : undefined}
          placeholderText="Add a date"
          onChange={handleOnChange}
          id={field.id}
          name={field.name}
          className={cn(
            'w-full rounded-8',
            showInvalidState && 'focus:!ring-red-9 focus:!border-red-9 border-0 ring-1 ring-red-9',
          )}
        />
        {showDescription && field.description && <p className="text-12 text-gray-10">{field.description}</p>}
      </div>
    );
  },
);

ContentCollectionBaseCustomFieldDateInput.displayName = 'ContentCollectionBaseCustomFieldDateInput';
